
import { useEffect, useState, useMemo } from 'react';
import { apiClient } from '@/lib/apiClient';

export type CountryCounts = Record<string, number>;

interface UseCountryLeadCounts {
  data: CountryCounts | null;
  loading: boolean;
  error: string | null;
  total: number;
}

export const useCountryLeadCounts = (userId?: string): UseCountryLeadCounts => {
  const [data, setData] = useState<CountryCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const endpoint = userId && userId !== 'all' 
          ? `/leadsys/count_by_country?user_id=${userId}`
          : '/leadsys/count_by_country';
        const res = await apiClient.get<CountryCounts>(endpoint);
        if (!mounted) return;
        setData(res || {});
        setError(null);
      } catch (e) {
        if (!mounted) return;
        const msg = e instanceof Error ? e.message : 'Failed to load country counts';
        setError(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [userId]);

  const total = useMemo(() => {
    if (!data) return 0;
    return Object.values(data).reduce((sum, v) => sum + v, 0);
  }, [data]);

  return { data, loading, error, total };
};
